import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { openModal, closeModal } from '../../../utils/modalUtils';

const ExpandedCard = ({ topic, onClose }) => {
  const { t } = useTranslation();
  const scrollYRef = useRef(0);

  useEffect(() => {
    scrollYRef.current = openModal();
    
    return () => {
      closeModal(scrollYRef.current);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const renderContent = (text) => {
    return text?.replace(/\[([^\]]+)\]\(tel:[^)]+\)/g, '$1') || '';
  };

  return (
    <motion.div
      className="fixed inset-0 z-[10001] bg-black/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-2xl mx-4 my-8 sm:my-12 bg-white rounded-2xl shadow-2xl overflow-hidden"
        key={topic.id}
        layoutId={`expandable-card-${topic.id}`}
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-primary via-teal-500 to-primary" />
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-border px-4 sm:px-6 py-4 flex items-start justify-between gap-4 z-10">
          <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground leading-tight pr-2">
            {topic.title}
          </h3>
          <button 
            onClick={onClose}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full 
                       bg-muted hover:bg-primary hover:text-white
                       text-muted-foreground transition-all duration-200"
            aria-label={t('close', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="px-4 sm:px-6 py-6 text-foreground">
          <div className="prose prose-sm sm:prose-base max-w-none 
                          prose-headings:text-foreground prose-headings:font-semibold
                          prose-p:text-foreground prose-p:leading-relaxed
                          prose-strong:text-primary prose-strong:font-semibold
                          prose-a:text-teal-600 prose-a:no-underline hover:prose-a:underline
                          prose-ul:my-4 prose-li:my-1
                          prose-li:marker:text-primary">
            <ReactMarkdown
              components={{
                strong: ({ children }) => (
                  <strong className="text-primary font-semibold">{children}</strong>
                ),
                a: ({ href, children }) => (
                  <a 
                    href={href} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-teal-600 hover:text-primary hover:underline transition-colors font-medium"
                  >
                    {children}
                  </a>
                ),
                ul: ({ children }) => (
                  <ul className="space-y-2 my-4 list-none pl-0">{children}</ul>
                ),
                li: ({ children }) => (
                  <li className="flex items-start gap-2 text-foreground">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span>{children}</span>
                  </li>
                ),
                p: ({ children }) => (
                  <p className="mb-4 leading-relaxed">{children}</p>
                ),
              }}
            >
              {renderContent(topic.expandedInfo)}
            </ReactMarkdown>
          </div>
        </div>

        <div className="h-4 sm:h-6" />
      </motion.div>
    </motion.div>
  );
};

export default ExpandedCard;
